/**
 * TopBottomPerformers — two columns showing top 3 and bottom 3 creatives
 * with verdict badge, creative name, and KPI label + value.
 * KPI label adapts per template's primary KPI.
 */

import { useVerdictSummary } from '@/hooks/useDashboard';
import { fmtKpiValue } from '@/lib/format';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function TopBottomPerformers() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);

  const kpiSummary = data?.kpiSummary;
  const primaryKpi = kpiSummary?.find((k) => k.isPrimary);

  // TopBottom data isn't in VerdictSummary type — we read from extended data
  const extendedData = data as Record<string, unknown> | undefined;
  const topPerformers = (extendedData?.topPerformers as Array<{
    creativeId: string;
    name: string;
    verdict: string;
    kpiValue: number;
  }>) ?? [];
  const bottomPerformers = (extendedData?.bottomPerformers as Array<{
    creativeId: string;
    name: string;
    verdict: string;
    kpiValue: number;
  }>) ?? [];

  if (topPerformers.length === 0 && bottomPerformers.length === 0) return null;

  const kpiLabel = primaryKpi?.label ?? 'KPI';
  const kpiUnit = primaryKpi?.unit ?? '';

  function formatKpiValue(value: number): string {
    return fmtKpiValue(value, kpiUnit);
  }

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-3">
      {/* Top Performers */}
      <div className="text-xs font-semibold text-primary mb-2" style={{ color: '#22C55E' }}>
        Top Performers
      </div>
      <div className="space-y-1.5 mb-3">
        {topPerformers.slice(0, 3).map((p) => {
          const color = VERDICT_COLORS[p.verdict] ?? '#8B95A7';
          return (
            <div key={p.creativeId} className="flex items-center gap-2">
              <span
                className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0"
                style={{ backgroundColor: `${color}20`, color }}
              >
                {p.verdict}
              </span>
              <span className="text-xs text-primary truncate flex-1">{p.name}</span>
              <span className="text-xs font-semibold flex-shrink-0" style={{ color }}>
                {kpiLabel}: {formatKpiValue(p.kpiValue)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom Performers */}
      <div
        className="text-xs font-semibold text-primary mb-2 pt-2 border-t border-border-subtle"
        style={{ color: '#EF4444' }}
      >
        Bottom Performers
      </div>
      <div className="space-y-1.5">
        {bottomPerformers.slice(0, 3).map((p) => {
          const color = VERDICT_COLORS[p.verdict] ?? '#8B95A7';
          return (
            <div key={p.creativeId} className="flex items-center gap-2">
              <span
                className="text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0"
                style={{ backgroundColor: `${color}20`, color }}
              >
                {p.verdict}
              </span>
              <span className="text-xs text-primary truncate flex-1">{p.name}</span>
              <span className="text-xs font-semibold text-danger flex-shrink-0">
                {kpiLabel}: {formatKpiValue(p.kpiValue)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
